import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { API_BASE } from "@/lib/api";
import AdBanner from "@/components/AdBanner";
import type { AppDetail } from "@/types/api";

interface CompareGame {
  appid: number;
  app: AppDetail;
  image: string;
  playerCount: number | null;
  reviewPositive: number;
  reviewTotal: number;
  achievements: number;
}

interface SearchResult {
  appid: number;
  name: string;
  image: string;
}

export default function ComparePage() {
  const { t } = useLang();
  const [games, setGames] = useState<CompareGame[]>([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadingGame, setLoadingGame] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API_BASE}/store/game-search?term=${encodeURIComponent(query)}&lang=ko`);
        const data = await res.json();
        setResults((data.items || []).filter((r: SearchResult) => !games.some((g) => g.appid === r.appid)));
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  }, [query, games]);

  const addGame = useCallback(async (appid: number, image: string) => {
    if (games.length >= 4 || games.some((g) => g.appid === appid)) return;
    setLoadingGame(true); setQuery(""); setResults([]);
    try {
      const [detailRes, playersRes, reviewsRes, achRes] = await Promise.allSettled([
        fetch(`${API_BASE}/store/appdetails?appids=${appid}&lang=ko`),
        fetch(`${API_BASE}/store/players?appid=${appid}`),
        fetch(`${API_BASE}/store/reviews?appid=${appid}&filter=all&num_per_page=0&language=all&lang=ko`),
        fetch(`${API_BASE}/store/achievements?appid=${appid}&lang=ko`),
      ]);
      let app: AppDetail | null = null;
      if (detailRes.status === "fulfilled") { const d = await detailRes.value.json(); if (d[appid]?.success) app = d[appid].data; }
      if (!app) { setLoadingGame(false); return; }
      let playerCount: number | null = null;
      if (playersRes.status === "fulfilled") { const d = await playersRes.value.json(); playerCount = d.response?.player_count ?? null; }
      let reviewPositive = 0, reviewTotal = 0;
      if (reviewsRes.status === "fulfilled") { const d = await reviewsRes.value.json(); const s = d.query_summary; if (s) { reviewPositive = s.total_positive || 0; reviewTotal = s.total_reviews || 0; } }
      let achievements = 0;
      if (achRes.status === "fulfilled") { const d = await achRes.value.json(); achievements = d.total || d.achievementpercentages?.achievements?.length || 0; }
      setGames((prev) => [...prev, { appid, app: app!, image, playerCount, reviewPositive, reviewTotal, achievements }]);
    } catch { /* */ }
    setLoadingGame(false);
  }, [games]);

  const removeGame = (appid: number) => setGames((prev) => prev.filter((g) => g.appid !== appid));
  const canAdd = games.length < 4 && !loadingGame;

  // Find best values for highlighting
  const bestPrice = games.length > 0 ? Math.min(...games.map((g) => g.app.is_free ? 0 : (g.app.price_overview?.final ?? Infinity))) : Infinity;
  const _bestDiscount = games.length > 0 ? Math.max(...games.map((g) => g.app.price_overview?.discount_percent ?? 0)) : 0; void _bestDiscount;
  const bestRating = games.length > 0 ? Math.max(...games.map((g) => g.reviewTotal > 0 ? (g.reviewPositive / g.reviewTotal) * 100 : 0)) : 0;
  const bestPlayers = games.length > 0 ? Math.max(...games.map((g) => g.playerCount ?? 0)) : 0;
  const bestMC = games.length > 0 ? Math.max(...games.map((g) => g.app.metacritic?.score ?? 0)) : 0;

  const isBest = (val: number, best: number) => val > 0 && val === best;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6 sm:py-8 flex-1 w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">{t.compare?.title || "게임 비교"}</h1>
        <p className="text-sm text-gray-500 dark:text-muted">{t.compare?.description || "4개의 게임을 검색하여 가격, 평점, 장르 등을 비교해보세요."}</p>
      </div>

      {/* Search + Selected chips */}
      <div className="relative mb-6">
        <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            </div>
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={canAdd ? (t.compare?.searchPlaceholder || "비교할 게임을 검색하세요...") : `${games.length}/4개까지 비교할 수 있어요`} disabled={!canAdd} className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-sub-text focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed" />
            {loadingGame && <div className="w-5 h-5 border-2 border-inner-border border-t-blue-500 rounded-full animate-spin" />}
            <span className="text-xs text-gray-400 dark:text-muted shrink-0">{games.length}/4</span>
          </div>

          {/* Selected game chips */}
          {games.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-inner-border dark:border-card-border">
              {games.map((g) => (
                <div key={g.appid} className="flex items-center gap-2 bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-xl pl-1.5 pr-2 py-1.5">
                  <img src={g.image || g.app.header_image} alt={g.app.name} width={48} height={22} className="rounded-md object-cover shrink-0" loading="lazy" />
                  <span className="text-xs font-medium text-gray-700 dark:text-foreground max-w-[120px] sm:max-w-[180px] truncate">{g.app.name}</span>
                  <button onClick={() => removeGame(g.appid)} className="w-5 h-5 rounded-full bg-gray-200 dark:bg-card-border hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 dark:text-muted transition-colors shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search dropdown */}
        {(results.length > 0 || searching) && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border shadow-lg z-50 max-h-[300px] overflow-y-auto">
            {searching ? <div className="py-6 text-center text-sm text-gray-400">검색 중...</div> : (
              results.slice(0, 8).map((r) => (
                <button key={r.appid} onClick={() => addGame(r.appid, r.image)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-inner-bg dark:hover:bg-hover-bg transition-colors text-left">
                  <img src={r.image} alt="" className="w-[60px] h-[28px] rounded object-cover bg-hover-bg shrink-0" />
                  <span className="text-sm text-gray-900 dark:text-white font-medium flex-1 truncate">{r.name}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Empty state */}
      {games.length === 0 && !loadingGame && (
        <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-hover-bg dark:bg-background flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </div>
          <p className="text-gray-500 dark:text-muted text-sm mb-1">{t.compare?.addGames || "비교할 게임을 추가해주세요"}</p>
          <p className="text-gray-400 dark:text-muted text-xs">위 검색창에서 게임을 검색하면 가격, 평점 등을 나란히 비교할 수 있어요.</p>
        </div>
      )}

      {/* Game cards + Comparison */}
      {games.length > 0 && (
        <>
          {/* Desktop: grid cards */}
          <div className={`hidden sm:grid gap-3 mb-4 grid-cols-${games.length}`}>
            {games.map((g) => {
              const p = g.app.price_overview;
              return (
                <div key={g.appid} className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden">
                  <div className="relative w-full aspect-[46/21] bg-hover-bg dark:bg-background">
                    <img src={g.app.header_image} alt={g.app.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate mb-1.5">{g.app.name}</h3>
                    <div className="flex items-center gap-2">
                      {p && p.discount_percent > 0 && <span className="text-[11px] font-bold text-white bg-emerald-500 px-1.5 py-0.5 rounded">-{p.discount_percent}%</span>}
                      {g.app.is_free ? (
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-200 dark:border-emerald-500/30">무료</span>
                      ) : p ? (
                        <>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">{p.final_formatted}</span>
                          {p.discount_percent > 0 && <span className="text-xs text-gray-400 dark:text-muted line-through">{p.initial_formatted}</span>}
                        </>
                      ) : null}
                    </div>
                    <Link to={`/game/${g.appid}`} className="inline-flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700 mt-2 transition-colors">
                      상세 정보 보기
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></svg>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile: card list per game with stats */}
          <div className="sm:hidden flex flex-col gap-3 mb-4">
            {games.map((g) => {
              const p = g.app.price_overview;
              const pct = g.reviewTotal > 0 ? Math.round((g.reviewPositive / g.reviewTotal) * 100) : 0;
              const pc = g.playerCount ?? 0;
              const mc = g.app.metacritic?.score ?? 0;
              return (
                <Link key={g.appid} to={`/game/${g.appid}`} className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden">
                  <div className="flex items-center">
                    <div className="relative shrink-0 w-[100px] h-[60px] bg-hover-bg dark:bg-background overflow-hidden">
                      <img src={g.app.header_image} alt={g.app.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0 px-3 py-2">
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white truncate">{g.app.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {p && p.discount_percent > 0 && <span className="text-[10px] font-bold text-white bg-emerald-500 px-1 py-0.5 rounded">-{p.discount_percent}%</span>}
                        {g.app.is_free ? (
                          <span className="text-[11px] font-bold text-emerald-600">무료</span>
                        ) : p ? (
                          <span className="text-[11px] font-bold text-gray-900 dark:text-white">{p.final_formatted}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-inner-border dark:bg-card-border border-t border-inner-border dark:border-card-border">
                    <div className="bg-card-bg dark:bg-card-bg px-2 py-2 text-center">
                      <p className="text-[9px] text-gray-400 dark:text-muted">평가</p>
                      <p className={`text-xs font-bold ${isBest(pct, bestRating) ? "text-emerald-600" : "text-gray-900 dark:text-white"}`}>{pct > 0 ? `${pct}%` : "-"}</p>
                    </div>
                    <div className="bg-card-bg dark:bg-card-bg px-2 py-2 text-center">
                      <p className="text-[9px] text-gray-400 dark:text-muted">접속자</p>
                      <p className={`text-xs font-bold ${isBest(pc, bestPlayers) ? "text-emerald-600" : "text-gray-900 dark:text-white"}`}>{pc > 0 ? (pc >= 1000 ? `${(pc / 1000).toFixed(pc >= 10000 ? 0 : 1)}K` : String(pc)) : "-"}</p>
                    </div>
                    <div className="bg-card-bg dark:bg-card-bg px-2 py-2 text-center">
                      <p className="text-[9px] text-gray-400 dark:text-muted">메타</p>
                      <p className={`text-xs font-bold ${isBest(mc, bestMC) ? "text-emerald-600" : "text-gray-900 dark:text-white"}`}>{mc > 0 ? mc : "-"}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Desktop comparison table */}
          <div className="hidden sm:block bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-inner-border dark:border-card-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">상세 비교</h2>
              </div>
            </div>

            {/* Header row */}
            <div className={`grid border-b border-inner-border dark:border-card-border bg-inner-bg/50 dark:bg-inner-bg grid-cols-[180px_repeat(${games.length},1fr)]`} style={{ gridTemplateColumns: `180px repeat(${games.length}, 1fr)` }}>
              <div className="px-4 py-3 text-xs font-semibold text-gray-400 dark:text-muted uppercase tracking-wider">항목</div>
              {games.map((g) => <div key={g.appid} className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-foreground truncate border-l border-inner-border dark:border-card-border">{g.app.name}</div>)}
            </div>

            {/* Rows */}
            {[
              { label: "현재 가격", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>, render: (g: CompareGame) => { const p = g.app.price_overview; const val = g.app.is_free ? 0 : (p?.final ?? Infinity); const best = isBest(val === 0 ? 0 : -val, -bestPrice); return { text: g.app.is_free ? "무료" : (p?.final_formatted || "-"), best }; } },
              { label: "메타크리틱", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>, render: (g: CompareGame) => { const mc = g.app.metacritic?.score ?? 0; return { text: mc > 0 ? String(mc) : "-", best: isBest(mc, bestMC), bold: mc > 0 }; } },
              { label: "유저 평가", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" /><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" /></svg>, render: (g: CompareGame) => { const pct = g.reviewTotal > 0 ? Math.round((g.reviewPositive / g.reviewTotal) * 100) : 0; return { text: pct > 0 ? `${pct}%` : "-", sub: g.reviewTotal > 0 ? `(${g.reviewTotal.toLocaleString("ko-KR")}개 리뷰)` : "", best: isBest(pct, bestRating), bold: pct > 0 }; } },
              { label: "현재 접속자", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>, render: (g: CompareGame) => { const pc = g.playerCount ?? 0; return { text: pc > 0 ? `${pc.toLocaleString("ko-KR")}명` : "-", best: isBest(pc, bestPlayers), bold: true }; } },
              { label: "장르", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>, render: (g: CompareGame) => ({ tags: g.app.genres?.map((genre) => genre.description) || [], best: false }) },
              { label: "출시일", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>, render: (g: CompareGame) => ({ text: g.app.release_date?.date || "-", best: false }) },
              { label: "개발사", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>, render: (g: CompareGame) => ({ text: g.app.developers?.[0] || "-", best: false }) },
              { label: "배급사", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>, render: (g: CompareGame) => ({ text: g.app.publishers?.[0] || "-", best: false }) },
            ].map((row) => (
              <div key={row.label} className="grid border-b border-inner-border dark:border-card-border last:border-b-0" style={{ gridTemplateColumns: `180px repeat(${games.length}, 1fr)` }}>
                <div className="px-4 py-3.5 flex items-center gap-2 text-sm text-gray-600 dark:text-muted bg-gray-50/30 dark:bg-inner-bg/30">
                  <span className="text-gray-400 dark:text-muted shrink-0">{row.icon}</span>
                  <span className="font-medium">{row.label}</span>
                </div>
                {games.map((g) => {
                  const r = row.render(g) as { text?: string; sub?: string; tags?: string[]; best: boolean; bold?: boolean };
                  return (
                    <div key={g.appid} className={`px-4 py-3.5 text-sm text-gray-800 dark:text-foreground border-l border-inner-border dark:border-card-border flex items-center ${r.best ? "bg-emerald-50/60 dark:bg-emerald-500/10 ring-1 ring-inset ring-emerald-200" : ""}`}>
                      <div className="min-w-0 w-full">
                        {r.tags ? (
                          <div className="flex flex-wrap gap-1">
                            {r.tags.map((tag) => <span key={tag} className="text-[11px] bg-hover-bg dark:bg-background text-gray-600 dark:text-muted px-2 py-0.5 rounded-md font-medium">{tag}</span>)}
                          </div>
                        ) : (
                          <>
                            <span className={r.bold ? "font-bold" : ""}>{r.text}</span>
                            {r.sub && <div className="text-[11px] text-gray-400 dark:text-muted mt-0.5">{r.sub}</div>}
                            {r.best && <span className="inline-block ml-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded align-middle">BEST</span>}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
      <AdBanner className="my-6" />
    </div>
  );
}
